import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils.js";

const baseUrl = "/api/dog/seme/prepoved_prodaje";

// Base thunks
export const fetchPrepovedProdajeSemena = createAsyncThunk(
  "prepovedProdajeSemena/list",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(baseUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchPrepovedProdajeSemenaPage = createAsyncThunk(
  "prepovedProdajeSemena/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchPrepovedProdajeSemenaById = createAsyncThunk(
  "prepovedProdajeSemena/get",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const savePrepovedProdajeSemena = createAsyncThunk(
  "prepovedProdajeSemena/save",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, dto);
      return response.data.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updatePrepovedProdajeSemena = createAsyncThunk(
  "prepovedProdajeSemena/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deletePrepovedProdajeSemena = createAsyncThunk(
  "prepovedProdajeSemena/delete",
  async ({ id, version }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.delete(
        `${baseUrl}/${id}?version=${version}`
      );
      return response.data.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Collect all thunks for isAnyOf matcher
const thunks = [
  fetchPrepovedProdajeSemena,
  fetchPrepovedProdajeSemenaPage,
  fetchPrepovedProdajeSemenaById,
  savePrepovedProdajeSemena,
  updatePrepovedProdajeSemena,
  deletePrepovedProdajeSemena,
];

const prepovedProdajeSemenaSlice = createSlice({
  name: "prepovedProdajeSemena",
  initialState: {
    list: null, // GET all resources
    current: null, // GET by ID / Update / Create
    page: null, // GET paginated resources
    error: null,
    isLoading: false,
  },
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
    },
    resetSaleProhibitionAssessment: (state) => {
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Individual fulfilled handlers for specific actions
      .addCase(fetchPrepovedProdajeSemena.fulfilled, (state, action) => {
        state.list = action.payload;
      })
      .addCase(fetchPrepovedProdajeSemenaPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(fetchPrepovedProdajeSemenaById.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(savePrepovedProdajeSemena.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(updatePrepovedProdajeSemena.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      // Batch handlers using isAnyOf for DRY code
      .addMatcher(
        isAnyOf(...thunks.map((t) => t.rejected)),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )
      .addMatcher(isAnyOf(...thunks.map((t) => t.pending)), (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isAnyOf(...thunks.map((t) => t.fulfilled)), (state) => {
        state.isLoading = false;
      });
  },
});

export const { clearCurrent, resetSaleProhibitionAssessment } =
  prepovedProdajeSemenaSlice.actions;

export default prepovedProdajeSemenaSlice.reducer;
